import ReactDOM from "react-dom/client";
import { Theme } from "@radix-ui/themes";
import { Suspense } from "react";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
} from "@tanstack/react-query";
import { Toaster } from "sonner";

import { RouterProvider, createRouter } from "@tanstack/react-router";
import { routeTree } from "./routes/__root.tsx";
import "./index.css";
import Loading from "./pages/loading.tsx";
import { AxiosError } from "axios";
import {
  STATUS_MESSAGES,
  STATUS_REDIRECTS,
} from "./shared/constants/type-vlue.ts";
import { showToast } from "./shared/utils/toast.ts";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import { initSentry } from "./shared/config/sentry.ts";

initSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof AxiosError) {
        const status = error.response?.status;
        if (!status) return;

        const msg = STATUS_MESSAGES[status];
        if (msg) {
          showToast.error({
            title: "Error",
            description: msg,
            duration: 5000,
          });
        }

        if (status === 401) {
          return;
        }

        const redirectTo = STATUS_REDIRECTS[status];
        if (redirectTo && window.location.pathname !== redirectTo) {
          router.navigate({ to: redirectTo });
        }
      }
    },
  }),
});

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <>
      <Theme>
        <Suspense fallback={<Loading />}>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </Suspense>
      </Theme>
      <Toaster position="bottom-right" />
    </>
  );
}
